// MODALS (Andrei, 2026-10-27): a seat joins a team without leaving the chart. Two ways in, one
// dialog: from a seat ("Add to a team" in the seat panel's Seat setup: pick the team) and from a
// team (the chart scoped to an empty team: pick the seat). Like the seat page's team picker, a
// DE on the seat is first shown its memory reach after the move (D8, `confirmReach`).
import {
  Field,
  FieldLabel,
  NativeSelect,
  NativeSelectOption,
  QuickAddDialog,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import { useReachConfirm } from "../memory/reach-confirm";
import { FormError } from "../structure/parts";
import { chartKeys, orgChartQuery } from "./chart-queries";

export type TeamMemberFor =
  | { kind: "seat"; id: string; title: string }
  | { kind: "team"; id: string; name: string };

export function TeamMemberDialog({
  api,
  co,
  of,
  onClose,
}: {
  api: Api;
  co: string;
  /** The fixed side: the seat (pick a team) or the team (pick a seat). */
  of: TeamMemberFor;
  onClose: () => void;
}) {
  const id = useId();
  const qc = useQueryClient();
  const chart = useQuery(orgChartQuery(api, co));
  const [pick, setPick] = useState("");
  const { confirmReach, reachDialog } = useReachConfirm(api, co);
  const members = chart.data?.team_members ?? [];
  const options =
    of.kind === "seat"
      ? (chart.data?.teams ?? [])
          .filter((t) => !members.some((m) => m.team_id === t.id && m.seat_id === of.id))
          .map((t) => ({ id: t.id, label: t.name }))
      : (chart.data?.seats ?? [])
          .filter(
            (s) =>
              s.status !== "closed" &&
              !members.some((m) => m.team_id === of.id && m.seat_id === s.id),
          )
          .map((s) => ({ id: s.id, label: s.title }));
  options.sort((a, b) => a.label.localeCompare(b.label));
  const add = useMutation({
    mutationFn: async () => {
      const seat = of.kind === "seat" ? of.id : pick;
      const team = of.kind === "team" ? of.id : pick;
      const ok = await confirmReach({
        kind: "seat_team",
        seat_id: seat,
        add_team_id: team,
        remove_team_id: null,
      });
      if (!ok) return false;
      await api.client.teams.addMember({ params: { co, id: team }, body: { seat_id: seat } });
      return true;
    },
    onSuccess: (added) => {
      if (!added) return;
      void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
      void qc.invalidateQueries({ queryKey: chartKeys.teams(co) });
      // Team KPIs roll up their member seats: the scores and each team's scorecard.
      void qc.invalidateQueries({ queryKey: ["company", co, "team-scores"] });
      void qc.invalidateQueries({ queryKey: ["company", co, "team"] });
      toast.add({
        title: of.kind === "seat" ? "Added to the team" : "Seat added to the team",
        type: "success",
      });
      onClose();
    },
  });
  const label = of.kind === "seat" ? "Team" : "Seat";
  return (
    <>
      {reachDialog}
      <QuickAddDialog
        title={of.kind === "seat" ? "Add to a team" : "Add a seat"}
        info="A seat in a team counts toward the team's scorecard and shows under it on the chart."
        description={of.kind === "seat" ? of.title : `To ${of.name}`}
        submitLabel="Add"
        canSubmit={pick !== ""}
        pending={add.isPending}
        onSubmit={() => add.mutate()}
        onClose={onClose}
        slot="team-member-dialog"
        error={<FormError what="Adding the seat to the team" error={add.error} />}
      >
        {chart.isPending ? null : options.length ? (
          <Field>
            <FieldLabel htmlFor={`${id}-pick`}>{label}</FieldLabel>
            <NativeSelect
              id={`${id}-pick`}
              className="w-full"
              value={pick}
              autoFocus
              onChange={(e) => setPick(e.target.value)}
            >
              <NativeSelectOption value="">
                {of.kind === "seat" ? "Pick a team…" : "Pick a seat…"}
              </NativeSelectOption>
              {options.map((o) => (
                <NativeSelectOption key={o.id} value={o.id}>
                  {o.label}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
        ) : (
          <p className="text-sm text-muted-foreground">
            {of.kind === "seat"
              ? "No team to add it to. Create teams in Setup › Teams."
              : "Every open seat is already in this team."}
          </p>
        )}
      </QuickAddDialog>
    </>
  );
}
